import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Loader2, TrendingUp, TrendingDown, Users, Briefcase, Receipt, Calendar as CalendarIcon, Wallet } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const MESES = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];
const fmt = (n) => (n ?? 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmtInt = (n) => (n ?? 0).toLocaleString("es-ES");

/** Dashboard Inversión Personal — KPIs coste interno + externo vs facturación con margen. */
export default function InversionPersonal() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState("year");  // "year" | "1".."12"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { year };
    if (month !== "year") params.month = parseInt(month, 10);
    api.get("/dashboard/inversion-personal", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year, month]);

  const yearOptions = useMemo(() => {
    const Y = new Date().getFullYear();
    return [Y, Y - 1, Y - 2, Y - 3];
  }, []);

  const maxBar = useMemo(() => {
    if (!data?.breakdown_mensual) return 1;
    return Math.max(1, ...data.breakdown_mensual.flatMap((b) => [b.coste_interno, b.coste_externo, b.facturacion]));
  }, [data]);

  return (
    <div className="p-6 max-w-[1800px] mx-auto" data-testid="inversion-personal-page">
      <PageHeader
        title="Inversión Personal vs Facturación"
        subtitle={`Coste real del equipo (interno + externo) vs lo facturado · margen bruto`}
      />

      <div className="flex flex-wrap items-end gap-3 mb-6">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Año</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800 h-10 w-32 text-sm" data-testid="inv-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              {yearOptions.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Periodo</Label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="bg-zinc-900 border-zinc-800 h-10 w-48 text-sm" data-testid="inv-month">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
              <SelectItem value="year">Año completo (YTD)</SelectItem>
              {MESES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m} {year}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-zinc-500" /></div>
      ) : !data ? (
        <div className="text-center py-12 text-zinc-500">Sin datos</div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="inv-kpis">
            <KPI label="Coste interno (nóminas)" value={fmt(data.kpis.coste_interno)}
              sub={`${fmtInt(data.kpis.n_nominas)} nómina(s)`} icon={Users} accent="amber" />
            <KPI label="Coste externo (autónomos)" value={fmt(data.kpis.coste_externo_autonomos)}
              sub={`${fmtInt(data.kpis.n_facturas_externas)} factura(s)`} icon={Briefcase} accent="violet" />
            <KPI label="Facturación" value={fmt(data.kpis.facturacion)}
              sub={`${fmtInt(data.kpis.n_peticiones)} petición(es)`} icon={Receipt} accent="sky" />
            <KPI
              label={data.kpis.margen >= 0 ? "Margen bruto" : "PÉRDIDA"}
              value={fmt(data.kpis.margen)}
              sub={data.kpis.margen_pct !== null ? `${data.kpis.margen_pct.toFixed(1)}%` : "—"}
              icon={data.kpis.margen >= 0 ? TrendingUp : TrendingDown}
              accent={data.kpis.margen >= 0 ? "emerald" : "red"}
              big
              testid="kpi-margen"
            />
          </div>

          {/* Estado pagos facturas externas */}
          {data.kpis.facturas_pendientes > 0 && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-6 text-sm flex items-center gap-3"
              data-testid="inv-facturas-pendientes">
              <Receipt className="w-4 h-4 text-amber-400" />
              <span className="text-amber-100">
                <b>{fmt(data.kpis.facturas_pendientes)}</b> en facturas externas pendientes de pago en este periodo.
              </span>
              <span className="text-zinc-500 ml-auto">Pagadas: {fmt(data.kpis.facturas_pagadas)}</span>
            </div>
          )}

          {/* Gráfico mensual */}
          {data.breakdown_mensual?.length > 0 && (
            <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 mb-6" data-testid="inv-chart">
              <div className="flex items-center gap-2 mb-3">
                <CalendarIcon className="w-4 h-4 text-zinc-400" />
                <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-200">
                  Evolución mensual {year}
                </h3>
                <div className="flex-1" />
                <Legend />
              </div>
              <div className="flex items-end gap-2 h-56">
                {data.breakdown_mensual.map((b, i) => {
                  const h_ci = (b.coste_interno / maxBar) * 100;
                  const h_ce = (b.coste_externo / maxBar) * 100;
                  const h_f = (b.facturacion / maxBar) * 100;
                  const margenNeg = b.margen < 0;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center min-w-0">
                      <div className="w-full flex items-end gap-0.5 h-44">
                        <div title={`Interno: ${fmt(b.coste_interno)}`}
                          className="flex-1 bg-amber-500/70 rounded-t" style={{ height: `${h_ci}%`, minHeight: h_ci > 0 ? "2px" : "0" }} />
                        <div title={`Externo: ${fmt(b.coste_externo)}`}
                          className="flex-1 bg-violet-500/70 rounded-t" style={{ height: `${h_ce}%`, minHeight: h_ce > 0 ? "2px" : "0" }} />
                        <div title={`Facturación: ${fmt(b.facturacion)}`}
                          className="flex-1 bg-sky-500/70 rounded-t" style={{ height: `${h_f}%`, minHeight: h_f > 0 ? "2px" : "0" }} />
                      </div>
                      <div className="text-[10px] text-zinc-500 mt-1">{MESES[i]}</div>
                      <div className={`text-[9px] font-mono ${margenNeg ? "text-red-400" : "text-emerald-400"}`}>
                        {b.margen >= 0 ? "+" : ""}{Math.round(b.margen / 1000)}k
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Tablas */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <TableCard title="Empleados internos" rows={data.empleados}
              icon={Users} testid="inv-tabla-empleados"
              empty="Sin nóminas en este periodo."
              render={(e) => (
                <>
                  <td className="p-2 text-zinc-100">{e.nombre}</td>
                  <td className="p-2 text-zinc-500 font-mono">{e.n_nominas}</td>
                  <td className="p-2 text-right font-mono font-semibold text-amber-300">{fmt(e.neto_total)}</td>
                </>
              )}
              headers={["Empleado", "Nóminas", "Total neto"]} />
            <TableCard title="Colaboradores externos" rows={data.colaboradores}
              icon={Briefcase} testid="inv-tabla-colaboradores"
              empty="Sin facturas externas en este periodo."
              render={(c) => (
                <>
                  <td className="p-2">
                    <div className="text-zinc-100">{c.emisor_nombre}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{c.emisor_nif || "—"}</div>
                  </td>
                  <td className="p-2 text-zinc-500 font-mono">{c.facturas}</td>
                  <td className="p-2 text-right">
                    <div className="font-mono font-semibold text-violet-300">{fmt(c.total)}</div>
                    {c.pendiente > 0 && (
                      <div className="text-[10px] text-amber-400">{fmt(c.pendiente)} pdte</div>
                    )}
                  </td>
                </>
              )}
              headers={["Colaborador", "Facturas", "Total"]} />
          </div>

          {/* Footer info */}
          <div className="text-[11px] text-zinc-500 mt-6 bg-zinc-900/40 rounded p-3 border border-zinc-800/60">
            ℹ️ <b>Facturación</b> = suma de <code>presupuesto_total</code> de peticiones en estado finalizada/cerrada/facturada/cobrada del periodo.
            <br />
            ℹ️ <b>Coste interno</b> = suma del líquido de las nóminas archivadas con `parsed_by_ai` o creadas manualmente.
            <br />
            ℹ️ <b>Coste externo</b> = suma del total de facturas externas (excluye rechazadas).
            <br />
            ℹ️ <b>Margen</b> = Facturación − (Coste interno + Coste externo).
          </div>
        </>
      )}
    </div>
  );
}


function KPI({ label, value, sub, icon: Icon, accent = "zinc", big = false, testid }) {
  const cls = {
    amber: "border-amber-500/30 text-amber-200 bg-amber-500/5",
    violet: "border-violet-500/30 text-violet-200 bg-violet-500/5",
    sky: "border-sky-500/30 text-sky-200 bg-sky-500/5",
    emerald: "border-emerald-500/40 text-emerald-100 bg-emerald-500/10",
    red: "border-red-500/40 text-red-100 bg-red-500/10",
    zinc: "border-zinc-700/50 text-zinc-100",
  }[accent];
  return (
    <div className={`border rounded-lg p-3 ${cls} ${big ? "ring-2 ring-offset-0 ring-current/30" : ""}`}
      data-testid={testid}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 opacity-80" />
        <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      </div>
      <div className={`font-bold font-mono ${big ? "text-3xl" : "text-2xl"}`}>{value}</div>
      <div className="text-[10px] opacity-60">{sub}</div>
    </div>
  );
}


function TableCard({ title, rows, headers, render, icon: Icon, empty, testid }) {
  return (
    <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-zinc-400" />
        <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-200">{title}</h3>
      </div>
      {!rows || rows.length === 0 ? (
        <div className="text-xs text-zinc-500 text-center py-6">{empty}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-zinc-950/40 text-[10px] uppercase tracking-wider text-zinc-500">
            <tr>
              {headers.map((h, i) => (
                <th key={i} className={`p-2 ${i === headers.length - 1 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-zinc-800/40 hover:bg-zinc-900/40">
                {render(r)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}


function Legend() {
  return (
    <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-zinc-400">
      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-500/70 rounded" /> Interno</span>
      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-violet-500/70 rounded" /> Externo</span>
      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-sky-500/70 rounded" /> Facturación</span>
    </div>
  );
}
